import axios, { AxiosRequestConfig, CancelToken, AxiosError } from 'axios';

export type ApiRequestConfig = AxiosRequestConfig;
export type ApiCancelToken = CancelToken;

export class Api {
  public static isAxiosError(error: unknown): error is AxiosError {
    return (
        error instanceof Error &&
        'isAxiosError' in error &&
        (error as Record<string, unknown>).isAxiosError === true
    );
  }

  public static isDetailedAxiosError<T extends { detailedError: { cause?: { message: string } } }>(
      error: unknown
  ): error is AxiosError<T> {
    return Api.isAxiosError(error) && error.response != null && 'detailedError' in error.response.data;
  }

  public abortController = () => axios.CancelToken.source();

  post = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
      url: string,
      data: Data,
      config?: ApiRequestConfig
  ) =>
      Api._doFetch<Response>(url, {
        method: 'POST',
        data,
        headers: {
          ...config?.headers,
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json;charset=UTF-8',
        },
        ...config,
      });

  put = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
      url: string,
      data: Data,
      config?: ApiRequestConfig
  ) =>
      Api._doFetch<Response>(url, {
        method: 'PUT',
        data,
        headers: {
          ...config?.headers,
          Accept: 'application/json',
          'Content-Type': 'application/json;charset=UTF-8',
        },
        ...config,
      });

  get = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
      url: string,
      data: Data,
      config?: ApiRequestConfig
  ) => {
    const params = new URLSearchParams(data).toString();
    const resultUrl = params ? `${url}?${params}` : `${url}`;
    return Api._doFetch<Response>(resultUrl, { method: 'GET', ...config });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private static _doFetch<Response = unknown>(url: string, init: ApiRequestConfig) {
    const resultUrl = /^\/\//.test(url as string) ? `https:${url}` : url;
    return axios(resultUrl, {
      validateStatus: status => status >= 200 && status < 300,
      ...init,
    }).then(response => response.data);
  }
}

export const api = new Api();